import React from 'react';
import "./RadioGroup.scss";
import { FormControlLabel, RadioGroup as BaseRadioGroup, Radio as BaseRadio } from "../../../shared/components";
import { DocumentedComponent } from '../../../shared/components/DocumentedComponent';
import clsx from 'clsx';
import _ from 'lodash';
import { getDataProps } from "../../../shared/utilities";
import { useAppSelector } from "../../../store";
import { useAppActions } from "../../../shared/hooks";

interface RadioGroupProps {
    name: string;
    [key: string]: any;
}

function RadioGroup(props: RadioGroupProps) {
    const { name, children, className, ...rest } = props;
    const parameterValues = useAppSelector(state => state.storyline.parameterValues);
    const { updateParameterValue } = useAppActions();
    const [value, setValue] = React.useState(parameterValues.get(name));

    const handleChange = (event: any) => {
        const newValue = JSON.parse(event.target.value);

        updateParameterValue(name, newValue);
        setValue(newValue);
    };

    React.useEffect(() => {

        if (parameterValues.has(name)) {
            const newValue = parameterValues.get(name);
            if (!_.isEqual(newValue, value)) {
                setValue(newValue);
            }
        }

    }, [parameterValues]);

    return (
        <BaseRadioGroup
            className={clsx("radio-group", className)}
            aria-label={name}
            name={name}
            value={JSON.stringify(value)}
            onChange={handleChange}
            {...rest}
            {...getDataProps(props)}
        >
            {children}
        </BaseRadioGroup>
    );
}

(RadioGroup as DocumentedComponent).metadata = {
    description: "The `RadioGroup` component contains two or more radio buttons, presenting the user with an option to select only one of these at a time.  Used as an alternative to a single-select dropdown or autocomplete, where the selected option is more important or needs to be displayed more prominently.",
    isSelfClosing: false,
    attributes: [
        { name: "name", type: "string", description: "The name of the variable that the selected value(s) will be read from and persisted to. The underlying component's `data-testid` attribute will be set to this value. This is useful for targeting the element via Playwright, Puppeteer or similar UI testing tools. Can be overridden via an explicit `data-testid` value." }
    ]
};

interface RadioProps {
    name?: string;
    value: string;
    label: string;
    [key: string]: any;
}

function Radio(props: RadioProps) {
    const { value, label, ...rest } = props;
    return <FormControlLabel value={JSON.stringify(value)} control={<BaseRadio color="primary" {...getDataProps(props)} />} label={label} {...rest} />;
}

(Radio as DocumentedComponent).metadata = {
    description: "The `Radio` component is a single radio button within a RadioGroup.  It is used to present the user with an option to select only one of these at a time.  Used as an alternative to a single-select dropdown or autocomplete, where the selected option is more important or needs to be displayed more prominently.",
    isSelfClosing: true,
    attributes: [
        { name: "value", type: "object", exactType: "any", description: "The value that will be stored in the variable specified by the parent RadioGroup's name attribute." },
        { name: "label", type: "string", description: "The text to display next to the radio button." }
    ]
}

export { RadioGroup, Radio };
